import React, { useState } from 'react';
import { payrollAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { money } from './_util';

const Field = ({ label, id, value, onChange, placeholder }) => (
  <label className="text-sm block">{label}
    <Input data-testid={id} value={value} onChange={onChange} placeholder={placeholder} />
  </label>
);

const Select = ({ label, id, value, onChange, options }) => (
  <label className="text-sm block">{label}
    <select data-testid={id} value={value} onChange={onChange}
      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </label>
);

const dollarsToCents = (v) => Math.round((Number(v) || 0) * 100);

export const CalculatorSection = () => {
  const [f, setF] = useState({
    pay_date: '2026-11-30', pay_frequency: 'semi-monthly', gross: '3000',
    supplemental: '0', supplemental_method: 'aggregate',
    // Federal W-4
    form_version: '2020', filing_status: 'single',
    allowances: '0', multiple_jobs: false,
    other_income: '0', deductions: '0', dependent_amount: '0', extra_withholding: '0',
    // State / local
    resident_state: 'OK', work_state: 'OK',
    state_filing_status: 'single', state_allowances: '0', state_extra_withholding: '0',
    localities: '',
  });
  // Section 125 (pre-tax) and post-tax deduction rows
  const [pretaxRows, setPretaxRows] = useState([{ code: 'sec125_medical', amount: '', reduces: 'federal_income,state_income,ss,medicare' }]);
  const [posttaxRows, setPosttaxRows] = useState([{ code: 'roth_401k', amount: '' }]);
  const [res, setRes] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const setRow = (rows, setRows) => (i, key, val) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r));
    setRows(next);
  };
  const addRow = (rows, setRows, blank) => () => setRows([...rows, blank]);
  const delRow = (rows, setRows) => (i) => setRows(rows.filter((_, idx) => idx !== i));

  const run = async () => {
    try {
      const w4 = {
        form_version: f.form_version,
        filing_status: f.filing_status,
        allowances: Number(f.allowances) || 0,
        multiple_jobs: !!f.multiple_jobs,
        other_income_cents: dollarsToCents(f.other_income),
        deductions_cents: dollarsToCents(f.deductions),
        dependent_amount_cents: dollarsToCents(f.dependent_amount),
        extra_withholding_cents: dollarsToCents(f.extra_withholding),
      };
      const pre_tax = pretaxRows
        .filter((r) => r.code && Number(r.amount) > 0)
        .map((r) => ({ code: r.code, kind: 'pre_tax', amount_cents: dollarsToCents(r.amount),
          reduces: (r.reduces || '').split(',').map((s) => s.trim()).filter(Boolean) }));
      const post_tax = posttaxRows
        .filter((r) => r.code && Number(r.amount) > 0)
        .map((r) => ({ code: r.code, amount_cents: dollarsToCents(r.amount) }));
      const r = await payrollAPI.taxCalculator({
        pay_date: f.pay_date, pay_frequency: f.pay_frequency, gross: Number(f.gross),
        supplemental: Number(f.supplemental), supplemental_method: f.supplemental_method,
        w4,
        state_setup: {
          work_state: f.work_state, resident_state: f.resident_state,
          filing_status: f.state_filing_status, allowances: Number(f.state_allowances) || 0,
          state_extra_withholding_cents: dollarsToCents(f.state_extra_withholding),
        },
        localities: f.localities.split(',').map((s) => s.trim()).filter(Boolean),
        pre_tax, post_tax,
      });
      setRes(r.data.result);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Calculation stopped (missing table)'); }
  };

  return (
    <Card data-testid="netpay-calculator">
      <CardHeader>
        <CardTitle>Test calculator</CardTitle>
        <CardDescription>Gross-to-net with the full method trace per line. Enter the complete W-4 (old or new form), state and local setup, and Section 125 / post-tax deductions. Nothing posts.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <div className="text-xs font-semibold uppercase text-slate-500 mb-2">Pay</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Pay date" id="calc-paydate" value={f.pay_date} onChange={set('pay_date')} />
            <Field label="Frequency" id="calc-freq" value={f.pay_frequency} onChange={set('pay_frequency')} />
            <Field label="Gross ($)" id="calc-gross" value={f.gross} onChange={set('gross')} />
            <Field label="Supplemental ($)" id="calc-supp" value={f.supplemental} onChange={set('supplemental')} />
            <Select label="Supplemental method" id="calc-supp-method" value={f.supplemental_method} onChange={set('supplemental_method')}
              options={[{ value: 'aggregate', label: 'Aggregate' }, { value: 'flat', label: 'Flat rate' }]} />
          </div>
        </div>

        <div>
          <div className="text-xs font-semibold uppercase text-slate-500 mb-2">Federal W-4</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Select label="W-4 form" id="calc-w4-version" value={f.form_version} onChange={set('form_version')}
              options={[{ value: '2020', label: '2020+ (new form)' }, { value: 'pre2020', label: 'Pre-2020 (old form)' }]} />
            <Select label="Filing status" id="calc-filing" value={f.filing_status} onChange={set('filing_status')}
              options={[{ value: 'single', label: 'Single' }, { value: 'married', label: 'Married (old form)' }, { value: 'mfj', label: 'Married filing jointly' }, { value: 'hoh', label: 'Head of household' }]} />
            {f.form_version === 'pre2020' ? (
              <Field label="Allowances / exemptions" id="calc-allowances" value={f.allowances} onChange={set('allowances')} />
            ) : (
              <>
                <label className="text-sm flex items-center gap-2 mt-6">
                  <input type="checkbox" data-testid="calc-multijobs" checked={f.multiple_jobs}
                    onChange={(e) => setF({ ...f, multiple_jobs: e.target.checked })} />
                  Step 2 — multiple jobs
                </label>
                <Field label="Step 3 — dependents ($)" id="calc-dependents" value={f.dependent_amount} onChange={set('dependent_amount')} />
                <Field label="Step 4a — other income ($)" id="calc-other-income" value={f.other_income} onChange={set('other_income')} />
                <Field label="Step 4b — deductions ($)" id="calc-deductions" value={f.deductions} onChange={set('deductions')} />
              </>
            )}
            <Field label="Step 4c — extra withholding ($)" id="calc-extra-wh" value={f.extra_withholding} onChange={set('extra_withholding')} />
          </div>
        </div>

        <div>
          <div className="text-xs font-semibold uppercase text-slate-500 mb-2">State &amp; local</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Resident state" id="calc-resident-state" value={f.resident_state} onChange={set('resident_state')} />
            <Field label="Work state" id="calc-workstate" value={f.work_state} onChange={set('work_state')} />
            <Select label="State filing status" id="calc-state-filing" value={f.state_filing_status} onChange={set('state_filing_status')}
              options={[{ value: 'single', label: 'Single' }, { value: 'married', label: 'Married' }, { value: 'mfj', label: 'Married filing jointly' }, { value: 'hoh', label: 'Head of household' }]} />
            <Field label="State allowances" id="calc-state-allowances" value={f.state_allowances} onChange={set('state_allowances')} />
            <Field label="State extra withholding ($)" id="calc-state-extra" value={f.state_extra_withholding} onChange={set('state_extra_withholding')} />
            <Field label="Local jurisdictions (comma-sep)" id="calc-localities" value={f.localities} onChange={set('localities')} placeholder="e.g. OH-COLUMBUS" />
          </div>
        </div>

        <div>
          <div className="text-xs font-semibold uppercase text-slate-500 mb-2">Section 125 (pre-tax) deductions</div>
          {pretaxRows.map((r, i) => (
            <div key={i} className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2 items-end" data-testid={`calc-pretax-row-${i}`}>
              <label className="text-sm">Code<Input data-testid={`calc-pretax-code-${i}`} value={r.code} onChange={(e) => setRow(pretaxRows, setPretaxRows)(i, 'code', e.target.value)} /></label>
              <label className="text-sm">Amount ($)<Input data-testid={`calc-pretax-amount-${i}`} value={r.amount} onChange={(e) => setRow(pretaxRows, setPretaxRows)(i, 'amount', e.target.value)} /></label>
              <label className="text-sm md:col-span-1">Reduces<Input data-testid={`calc-pretax-reduces-${i}`} value={r.reduces} onChange={(e) => setRow(pretaxRows, setPretaxRows)(i, 'reduces', e.target.value)} /></label>
              <Button type="button" variant="outline" size="sm" data-testid={`calc-pretax-del-${i}`} onClick={() => delRow(pretaxRows, setPretaxRows)(i)}>Remove</Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" data-testid="calc-pretax-add" onClick={addRow(pretaxRows, setPretaxRows, { code: '', amount: '', reduces: 'federal_income,state_income' })}>Add pre-tax deduction</Button>
        </div>

        <div>
          <div className="text-xs font-semibold uppercase text-slate-500 mb-2">Post-tax deductions</div>
          {posttaxRows.map((r, i) => (
            <div key={i} className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-2 items-end" data-testid={`calc-posttax-row-${i}`}>
              <label className="text-sm">Code<Input data-testid={`calc-posttax-code-${i}`} value={r.code} onChange={(e) => setRow(posttaxRows, setPosttaxRows)(i, 'code', e.target.value)} /></label>
              <label className="text-sm">Amount ($)<Input data-testid={`calc-posttax-amount-${i}`} value={r.amount} onChange={(e) => setRow(posttaxRows, setPosttaxRows)(i, 'amount', e.target.value)} /></label>
              <Button type="button" variant="outline" size="sm" data-testid={`calc-posttax-del-${i}`} onClick={() => delRow(posttaxRows, setPosttaxRows)(i)}>Remove</Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" data-testid="calc-posttax-add" onClick={addRow(posttaxRows, setPosttaxRows, { code: '', amount: '' })}>Add post-tax deduction</Button>
        </div>

        <Button data-testid="calc-run" onClick={run}>Calculate</Button>
        {res && (
          <div className="text-sm border rounded p-3" data-testid="calc-result">
            <div className="font-semibold">Gross {money(res.gross_cents)} → Net {money(res.net_pay_cents)}</div>
            <table className="mt-2 w-full">
              <tbody>
                {(res.employee_taxes || []).map((t) => (
                  <tr key={t.tax} data-testid={`calc-tax-${t.tax}`}>
                    <td className="py-0.5">{t.tax}</td>
                    <td className="text-right">{money(t.amount_cents)}</td>
                    <td className="pl-3 text-xs text-slate-500">{t.trace?.method || ''}</td>
                  </tr>
                ))}
                {(res.pre_tax_deductions || []).map((d) => (
                  <tr key={`pre-${d.code}`} className="text-slate-600"><td>pre-tax {d.code}</td><td className="text-right">{money(d.amount_cents)}</td><td /></tr>
                ))}
                {(res.post_tax_deductions || []).map((d) => (
                  <tr key={`d-${d.code}`} className="text-slate-600"><td>post-tax {d.code}</td><td className="text-right">{money(d.amount_cents)}</td><td /></tr>
                ))}
                {(res.garnishments || []).map((g, i) => (
                  <tr key={`g-${i}`} className="text-slate-600"><td>garnishment {g.case_number || ''}</td><td className="text-right">{money(g.withheld_cents)}</td><td className="pl-3 text-xs">{g.ccpa_capped ? 'CCPA capped' : ''}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
